import type { UserProfile } from "@/domains/pm/types";
import type { SigningChainSigner } from "./repository";

/**
 * The ordered signer chain as the Sign dialog edits it: one entry per POSITION,
 * each holding the `user_profiles.id`s who sign at it.
 *
 * A position with more than one member is a PARALLEL GROUP — they may sign in
 * any order and the chain advances only once all of them have signed. That is
 * why the wire payload always carries explicit positions: array order alone
 * cannot express two people at the same step, and `dokobit-sign-start` refuses a
 * half-positioned list (`assignPositions`: either every entry carries one or
 * none does).
 *
 * Nothing here is stateful — every operation returns a new chain, and an
 * operation that cannot apply returns the chain it was given.
 */
export type SignerChain = number[][];

/** `dokobit-sign-start` refuses more than this (`MAX_SIGNERS`). It counts the
 *  WHOLE `signers` array it is sent — which `toSignerPayload` always opens with
 *  the creator — so it is a limit on the signing, not on the picker. */
export const MAX_CHAIN_SIGNERS = 10;

/**
 * How many people the chain may hold. The creator occupies one of the ten
 * without ever being a step, so a picker capped at `MAX_CHAIN_SIGNERS` would
 * fill to an advertised maximum that is one entry too many for the request it
 * builds — a 400 at Sign, after the work of composing it.
 */
export const MAX_CHAIN_INVITEES = MAX_CHAIN_SIGNERS - 1;

/** Every signer in the chain, in signing order. */
export function chainUserIds(chain: SignerChain): number[] {
  return chain.flat();
}

export function chainSize(chain: SignerChain): number {
  return chain.reduce((total, group) => total + group.length, 0);
}

/** 1-based position of `userId`, or null when they are not in the chain. */
export function positionOf(chain: SignerChain, userId: number): number | null {
  const index = chain.findIndex((group) => group.includes(userId));
  return index === -1 ? null : index + 1;
}

/** Append a signer at a position of their own. No-op if already present or full. */
export function addSigner(chain: SignerChain, userId: number): SignerChain {
  if (positionOf(chain, userId) !== null) return chain;
  if (chainSize(chain) >= MAX_CHAIN_INVITEES) return chain;
  return [...chain, [userId]];
}

/** Drop a signer; a position left empty collapses so the steps stay contiguous. */
export function removeSigner(chain: SignerChain, userId: number): SignerChain {
  return chain
    .map((group) => group.filter((id) => id !== userId))
    .filter((group) => group.length > 0);
}

/**
 * Reconcile the chain against a flat selection — what the member multi-select
 * hands back. Signers already in the chain keep their position and their group;
 * newly ticked ones land at the end, one step each.
 */
export function syncChain(chain: SignerChain, selectedIds: number[]): SignerChain {
  const selected = new Set(selectedIds);
  const kept = chain
    .map((group) => group.filter((id) => selected.has(id)))
    .filter((group) => group.length > 0);
  const present = new Set(chainUserIds(kept));
  return selectedIds.reduce(
    (acc, id) => (present.has(id) ? acc : addSigner(acc, id)),
    kept as SignerChain
  );
}

/** Swap a position with its neighbour. `delta` is -1 (earlier) or +1 (later). */
export function moveGroup(chain: SignerChain, index: number, delta: number): SignerChain {
  const target = index + delta;
  if (index < 0 || index >= chain.length) return chain;
  if (target < 0 || target >= chain.length) return chain;
  const next = [...chain];
  next[index] = chain[target];
  next[target] = chain[index];
  return next;
}

/**
 * Merge a position into the one before it: the two become a parallel group who
 * sign in any order. The first position has nothing to merge into.
 */
export function mergeWithPrevious(chain: SignerChain, index: number): SignerChain {
  if (index <= 0 || index >= chain.length) return chain;
  const next = [...chain];
  next[index - 1] = [...chain[index - 1], ...chain[index]];
  next.splice(index, 1);
  return next;
}

/** Pull one signer out of a parallel group into a step of their own, right after it. */
export function splitSigner(chain: SignerChain, userId: number): SignerChain {
  const index = chain.findIndex((group) => group.includes(userId));
  if (index === -1 || chain[index].length < 2) return chain;
  const next = [...chain];
  next[index] = chain[index].filter((id) => id !== userId);
  next.splice(index + 1, 0, [userId]);
  return next;
}

/**
 * The `signers` array for `dokobit-sign-start`. An empty chain yields `null`,
 * not `[]` — the caller must then omit the field entirely, which is the
 * byte-identical legacy request that signs alone. (`[]` would be a 400: the
 * backend requires 1–10 entries once the key is present.)
 */
export function toSignerPayload(
  chain: SignerChain,
  creatorId: number | null
): SigningChainSigner[] | null {
  if (chain.length === 0 || creatorId == null) return null;
  // The creator is not a STEP — they sign first, right now, in the dialog they
  // are standing in. The steps are everyone the document goes to after them, so
  // the chain the user composed starts at position 2 on the wire.
  //
  // The server independently requires the caller to be among the signers, so
  // this is where that requirement is met rather than something the user has to
  // remember to do.
  return [
    { user_id: creatorId, position: 1 },
    ...chain.flatMap((group, index) =>
      group.map((userId) => ({ user_id: userId, position: index + 2 }))
    ),
  ];
}

/** How a signer is named in the chain — the profile name, else their email, else
 *  the fallback: a nameless profile whose email is missing has nothing to render,
 *  and `""` would print as "You ()". */
export function signerDisplayName(user: UserProfile | undefined, fallback: string): string {
  if (!user) return fallback;
  const name = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return name || user.email || fallback;
}
